import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, from, Observable, retry, Subject, timer } from 'rxjs';
import { ApiEnv } from '../../../../models';
import { EnvSpecific } from '../../../_models';
import { MaintenanceHelperService } from './maintenance-helper.service';

/** Token to skip auth/api-url interceptors for environment loading requests */
export const SKIP_INTERCEPTORS = new HttpContextToken<boolean>(() => false);

// prettier-ignore
@Injectable({ providedIn: 'root' })
export class EnvironmentSpecificService {
  envSpecificAsync: Promise<EnvSpecific>;

  /** @deprecated 01/16/2025 Use envSpecificAsync */
  envSpecific: EnvSpecific;
  /** @deprecated 01/16/2025 Use envSpecificAsync */
  envSpecificSubject: BehaviorSubject<EnvSpecific> = new BehaviorSubject<EnvSpecific>(null);
  /** @deprecated 01/16/2025 Use envSpecificAsync */
  envHasBeenLoaded$: Subject<EnvSpecific> = new Subject();

  constructor(
    private http: HttpClient,
    private maintenanceHelperService: MaintenanceHelperService
  ) {
    this.initialize();
  }

  /** @deprecated 01/16/2025 Use envSpecificAsync */
  getEnv(): Observable<EnvSpecific> { return from(this.getEnvPromise()); }

  /** @deprecated 01/16/2025 Use envSpecificAsync */
  getEnvPromise(): Promise<EnvSpecific> { return this.envSpecificAsync; }

  /** @deprecated Use envSpecificAsync */
  async resolve(): Promise<EnvSpecific> { return this.envSpecificAsync; }

  private initialize(): void {
    // eslint-disable-next-line @typescript-eslint/no-misused-promises, no-async-promise-executor
    this.envSpecificAsync = new Promise(async (resolve, reject) => {
      try {
        const apiEnv = await firstValueFrom(
          this.http.get('./assets/env.json?v=' + this.getRandomNumberForUrl(), {
            context: new HttpContext().set(SKIP_INTERCEPTORS, true),
          })
        ) as ApiEnv;

        const rawEnvFromDb = await firstValueFrom(
          this.http.get(apiEnv.apiUrl + '/environment?env=' + apiEnv.environment, {
            context: new HttpContext().set(SKIP_INTERCEPTORS, true),
          }).pipe(
            retry({
              count: 20, // Maximum 20 retries (approximately 30 seconds total)
              delay: (error, retryCount) => {
                // Increasing backoff: 500ms, 1s, 1.5s, 2s, etc. (capped at 2s)
                const delayMs = Math.min(500 * retryCount, 2000);
                return timer(delayMs);
              }
            })
          )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ) as any[];

        const isLocalEnv = apiEnv.environment.toUpperCase() === 'LOCAL';

        const envSpecific = rawEnvFromDb.reduce(
          (objects, element) => {
            if (!(isLocalEnv && objects[element.AzzlyKey])) {
              objects[element.AzzlyKey] = element.AzzlyValue;
            }
            return objects;
          },
          apiEnv
        ) as EnvSpecific;

        envSpecific.apiUrl = apiEnv.apiUrl;
        if (apiEnv.reportsUrl) {
          envSpecific.reportsUrl = apiEnv.reportsUrl;
        }

        // compatibility
        this.envSpecific = envSpecific;
        this.envSpecificSubject.next(envSpecific);
        this.envHasBeenLoaded$.next(this.envSpecific);
        // /compatibility

        resolve(envSpecific);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        if (e.status === 503 && !this.maintenanceHelperService.checkIfUnderMaintenance(e)) {
          alert('There was a problem loading the application. Please try again momentarily.');
        }
        reject(e);
      }
    });
  }

  private getRandomNumberForUrl(): string {
    const min = 11111;
    const max = 99999;
    return Math.floor(Math.random() * (max - min) + min).toString();
  }
}
